// Render-only sheet body for MeasurementCalculatorSheet. Extracted
// from the main file to keep that file under the max-lines policy.
// Owns no state, no side-effects — the parent passes in dims, the
// active index, computed result, and the action callbacks.

import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  RefObject,
} from 'react'

function previewDimension(value: string): string {
  return value === '' ? '—' : value
}

const DIGIT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const
const DIGIT_BUTTON_CLASS =
  'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-white text-xl font-semibold text-gray-900 active:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]'
const DECIMAL_BUTTON_CLASS =
  'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-white text-4xl font-bold leading-none text-gray-900 pb-2 active:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-40 disabled:cursor-not-allowed'
const AUX_BUTTON_CLASS =
  'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-base font-medium text-gray-800 active:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-40 disabled:cursor-not-allowed'
const APPLY_BUTTON_CLASS =
  'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg bg-[color:var(--color-brand)] text-base font-semibold text-[color:var(--color-brand-foreground)] active:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-40 disabled:cursor-not-allowed'
const DIM_BOX_BASE_CLASS =
  'inline-flex min-h-14 w-full items-center justify-center rounded-lg border bg-white px-2 py-2 text-lg font-semibold tabular-nums focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]'
const DIM_BOX_ACTIVE = 'border-[color:var(--color-brand)] ring-2 ring-[color:var(--color-brand)]/30'
const DIM_BOX_INACTIVE = 'border-gray-200'

export interface MeasurementCalculatorSheetContentProps {
  sheetRef: RefObject<HTMLDivElement | null>
  dialogAriaProps: Record<string, string>
  titleId: string | undefined
  title: string | undefined
  currentLine: string | null
  dims: readonly string[]
  activeIdx: number
  dimensionLabels: readonly string[]
  previewParts: string
  resultPreview: string
  unitSuffix: string
  canApply: boolean
  onOverlayClick: (e: ReactMouseEvent<HTMLDivElement>) => void
  onSheetKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void
  onSelectDim: (idx: number) => void
  onAppendDigit: (digit: string) => void
  onAppendDecimal: () => void
  onBackspace: () => void
  onClearAll: () => void
  onClose: () => void
  onApply: () => void
}

export function MeasurementCalculatorSheetContent({
  sheetRef,
  dialogAriaProps,
  titleId,
  title,
  currentLine,
  dims,
  activeIdx,
  dimensionLabels,
  previewParts,
  resultPreview,
  unitSuffix,
  canApply,
  onOverlayClick,
  onSheetKeyDown,
  onSelectDim,
  onAppendDigit,
  onAppendDecimal,
  onBackspace,
  onClearAll,
  onClose,
  onApply,
}: MeasurementCalculatorSheetContentProps): ReactElement {
  const activeValue = dims[activeIdx] ?? ''
  return (
    <>
      <div
        data-testid="measurement-calculator-overlay"
        onClick={onOverlayClick}
        className="fixed inset-0 z-40 bg-black/50"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        {...dialogAriaProps}
        tabIndex={-1}
        onKeyDown={onSheetKeyDown}
        data-testid="measurement-calculator-sheet"
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-gray-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl focus:outline-none"
      >
        {title !== undefined ? (
          <h2 id={titleId} className="mb-1 text-sm font-semibold text-gray-700">
            {title}
          </h2>
        ) : null}
        {currentLine !== null ? (
          <p
            data-testid="measurement-calculator-current"
            className="mb-2 text-xs text-gray-500"
          >
            {currentLine}
          </p>
        ) : null}
        <div
          data-testid="measurement-calculator-dim-row"
          className="mb-2 flex items-stretch gap-2"
        >
          {dims.map((dimValue, idx) => {
            const isActive = idx === activeIdx
            const label = dimensionLabels[idx] ?? `Dimension ${idx + 1}`
            return (
              <div key={idx} className="flex flex-1 flex-col">
                <span
                  aria-hidden="true"
                  data-testid={`measurement-calculator-dim-label-${idx}`}
                  className="mb-1 text-xs font-medium text-gray-600"
                >
                  {label}
                </span>
                <button
                  type="button"
                  onClick={() => onSelectDim(idx)}
                  aria-label={`${label}: ${
                    dimValue === '' ? 'empty' : dimValue
                  }`}
                  aria-pressed={isActive}
                  data-testid={`measurement-calculator-dim-${idx}`}
                  className={`${DIM_BOX_BASE_CLASS} ${isActive ? DIM_BOX_ACTIVE : DIM_BOX_INACTIVE}`}
                >
                  <span className="sr-only">{label}: </span>
                  <span aria-hidden="true">{previewDimension(dimValue)}</span>
                </button>
              </div>
            )
          })}
        </div>
        <div
          data-testid="measurement-calculator-preview"
          className="mb-3 flex min-h-14 items-center justify-end gap-2 rounded-md bg-gray-50 px-4 py-2 text-2xl font-semibold tabular-nums text-gray-900"
        >
          <span className="text-sm font-normal text-gray-500">
            {previewParts} =
          </span>
          <span>
            {resultPreview}
            {unitSuffix}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {DIGIT_KEYS.map((digit) => (
            <button
              key={digit}
              type="button"
              aria-label={`Enter ${digit}`}
              onClick={() => onAppendDigit(digit)}
              className={DIGIT_BUTTON_CLASS}
            >
              {digit}
            </button>
          ))}
          <button
            type="button"
            aria-label="Enter decimal point"
            onClick={onAppendDecimal}
            disabled={activeValue.includes('.')}
            className={DECIMAL_BUTTON_CLASS}
          >
            .
          </button>
          <button
            type="button"
            aria-label="Enter 0"
            onClick={() => onAppendDigit('0')}
            className={DIGIT_BUTTON_CLASS}
          >
            0
          </button>
          <button
            type="button"
            aria-label="Backspace"
            onClick={onBackspace}
            disabled={activeValue === ''}
            className={AUX_BUTTON_CLASS}
          >
            ⌫
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className={`${AUX_BUTTON_CLASS} flex-1`}
            aria-label="Cancel measurement"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onClearAll}
            disabled={dims.every((d) => d === '')}
            className={`${AUX_BUTTON_CLASS} flex-1`}
            aria-label="Clear all dimensions"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={!canApply}
            className={`${APPLY_BUTTON_CLASS} flex-1`}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  )
}
